import { newId } from './ids';
import type { Column, ColumnRole, Row, TemplateDoc, TestRow } from './template';

/**
 * Pure editing operations for the template builder grid. Every function returns a new
 * document, so the UI gets undo/redo by keeping previous versions.
 */

export function move<T>(list: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || from >= list.length) return [...list];
  const out = [...list];
  const [item] = out.splice(from, 1);
  out.splice(Math.max(0, Math.min(to, out.length)), 0, item as T);
  return out;
}

export function newTestRow(partial: Partial<TestRow> = {}): TestRow {
  return {
    id: newId(),
    kind: 'test',
    code: partial.code ?? 'TEST',
    label: partial.label ?? 'New investigation',
    valueType: 'numeric',
    required: false,
    ranges: [],
    ...partial,
  };
}

function col(role: ColumnRole, label: string): Column {
  return { id: newId(), role, label };
}

/** Five columns and one test row under the header, as specified for new templates. */
export function blankTemplate(name = 'Untitled template'): TemplateDoc {
  return {
    name,
    aliases: [],
    tags: [],
    columns: [
      col('investigation', 'Investigation'),
      col('result', 'Result'),
      col('ref_min', 'Reference Value Min'),
      col('ref_max', 'Reference Value Max'),
      col('unit', 'Unit'),
    ],
    rows: [newTestRow({ code: 'TEST1' })],
    extraFields: [],
    sensitive: false,
    separateReport: false,
    performedBy: { kind: 'in_house' },
    print: { newPage: false, mergeRefColumns: true },
  };
}

/** Four columns for "Other (generic)" reports: the reference value is free text. */
export function blankGenericTable(): TemplateDoc {
  const t = blankTemplate('Generic report');
  return {
    ...t,
    columns: [
      col('investigation', 'Investigation'),
      col('result', 'Result'),
      col('ref_range', 'Reference Value'),
      col('unit', 'Unit'),
    ],
    rows: [newTestRow({ code: 'TEST1', valueType: 'text', ranges: [{ type: 'text' }] })],
  };
}

export function insertRow(doc: TemplateDoc, at: number, row: Row = nextTestRow(doc)): TemplateDoc {
  const rows = [...doc.rows];
  rows.splice(Math.max(0, Math.min(at, rows.length)), 0, row);
  return { ...doc, rows };
}

export function removeRow(doc: TemplateDoc, rowId: string): TemplateDoc {
  return { ...doc, rows: doc.rows.filter((r) => r.id !== rowId) };
}

export function moveRow(doc: TemplateDoc, from: number, to: number): TemplateDoc {
  return { ...doc, rows: move(doc.rows, from, to) };
}

export function insertColumn(doc: TemplateDoc, at: number, label = 'New column'): TemplateDoc {
  const columns = [...doc.columns];
  columns.splice(Math.max(0, Math.min(at, columns.length)), 0, col('custom', label));
  return { ...doc, columns };
}

/** Removing the only Result column is refused; flagging depends on it. */
export function removeColumn(doc: TemplateDoc, columnId: string): TemplateDoc {
  const target = doc.columns.find((c) => c.id === columnId);
  if (!target) return doc;
  if (target.role === 'result' && doc.columns.filter((c) => c.role === 'result').length === 1) {
    throw new Error('A template needs a Result column');
  }
  const rows = doc.rows.map((r) => {
    if (!r.cells || !(columnId in r.cells)) return r;
    const { [columnId]: _, ...rest } = r.cells;
    return { ...r, cells: rest };
  });
  return { ...doc, columns: doc.columns.filter((c) => c.id !== columnId), rows };
}

export function moveColumn(doc: TemplateDoc, from: number, to: number): TemplateDoc {
  return { ...doc, columns: move(doc.columns, from, to) };
}

/**
 * Swap the free-text contents of two custom cells (drag a cell onto another).
 * Cells in role columns (result, ranges, unit) are defined by row settings, not free text.
 */
export function swapCells(
  doc: TemplateDoc,
  a: { rowId: string; columnId: string },
  b: { rowId: string; columnId: string },
): TemplateDoc {
  const isCustom = (id: string) => doc.columns.find((c) => c.id === id)?.role === 'custom';
  if (!isCustom(a.columnId) || !isCustom(b.columnId)) {
    throw new Error('Only cells in custom columns can be moved');
  }
  const get = (p: typeof a) => doc.rows.find((r) => r.id === p.rowId)?.cells?.[p.columnId] ?? '';
  const va = get(a);
  const vb = get(b);
  const rows = doc.rows.map((r) => {
    let cells = r.cells;
    if (r.id === a.rowId) cells = { ...cells, [a.columnId]: vb };
    if (r.id === b.rowId) cells = { ...(r.id === a.rowId ? cells : r.cells), [b.columnId]: va };
    return cells === r.cells ? r : { ...r, cells };
  });
  return { ...doc, rows };
}

function nextTestRow(doc: TemplateDoc): TestRow {
  const codes = new Set(doc.rows.flatMap((r) => (r.kind === 'test' ? [r.code] : [])));
  let n = codes.size + 1;
  while (codes.has(`TEST${n}`)) n++;
  return newTestRow({ code: `TEST${n}` });
}
